import {
  AxisY,
  ChartRow,
  SectionGraph,
  SectionSessionDetails,
  Sort,
} from "@/app/report/common/constructor"
import { TypeChecker } from "@/app/report/common/tools"
import { DataTable, TableRow } from "@/app/report/common/type"
import { durationToNumber, localeStringToNumber } from "@/utils/helper"
import type { ReportSortSetting } from "@/stores/reportSort"

// 화면(그래프/테이블)·PDF export가 동일 기준으로 정렬하기 위한 공용 로직.
// (ChartByPlayerSection / 구 StatsByPlayerSection 의 정렬 로직을 이관. Stats 는 폐지(#99), Custom Table 은 useCustomTableData.sortCustomTableRows 사용)
export type PositionRankFn = (code?: string | number) => number

export function sortChartRows(
  rows: ChartRow[],
  axisY: AxisY[],
  sort: ReportSortSetting,
  positionRank: PositionRankFn,
): ChartRow[] {
  const axisIndex = axisY.findIndex((y) => y.key === sort.key)

  const getValue = (row: ChartRow): number | string => {
    if (axisIndex !== -1) {
      return row.data[axisIndex] || 0
    } else if (sort.key === "playerName") {
      return row.header.get("playerName") as string
    } else if (sort.key === "position") {
      // 백엔드 정렬 순서 신뢰. 기존 표시(GK-우선)를 유지하려고 rank 를 음수로 반환(asc 시 GK 먼저).
      return -positionRank(row.header.get("position") as string)
    } else if (sort.key === "date") {
      return row.header.get("date") as string
    } else if (row.header.has(sort.key)) {
      return (row.header.get(sort.key) as number | string) || 0
    }

    return 0
  }

  const asc = sort.order === "asc"

  return [...rows].sort((a, b) => {
    const aValue = getValue(a)
    const bValue = getValue(b)

    if (sort.key === "date") {
      // Activity 그래프: 날짜는 시간축(Date) 기준 비교
      const aTime = new Date(aValue as string).getTime()
      const bTime = new Date(bValue as string).getTime()

      return asc ? aTime - bTime : bTime - aTime
    }

    if (typeof aValue === "string" && typeof bValue === "string") {
      return asc ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue)
    }

    return asc ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number)
  })
}

// 결측 셀(null/undefined/빈 문자열) 비교 — 정렬 방향과 무관하게 항상 뒤로.
// (정렬 목적상 데이터 있는 행이 먼저 — MUI/엑셀/SQL NULLS LAST 관례, Custom Table 비교기와 동일 정책)
// 결측이 아니면 null 반환(후속 비교로 진행). 기존엔 결측 분기가 없어 null 이 섞인 컬럼에서
// 비교기가 0/NaN 을 반환해 정렬이 통째로 무력화됐음 (WEB-1774)
function compareMissingCell(
  aVal: string | number | null,
  bVal: string | number | null,
): number | null {
  const aMissing = aVal === null || aVal === undefined || aVal === ""
  const bMissing = bVal === null || bVal === undefined || bVal === ""

  if (aMissing && bMissing) {
    return 0
  }
  if (aMissing) {
    return 1
  }
  if (bMissing) {
    return -1
  }

  return null
}

// 숫자형 또는 로케일 숫자 문자열("1,234.5"/"72"/"0.0")을 수치로. 아니면 NaN.
function toNumericCell(val: string | number): number {
  return TypeChecker.isNumber(val) ? val : localeStringToNumber(String(val))
}

// 필드 특수 분기에 해당하지 않는 일반 셀 값 비교: duration("12'34") → 수치(콤마 인식) → 그 외 0.
// 기존 게이트(!isNaN(Number(v)))는 콤마 문자열을 숫자로 인식하지 못해
// "72" vs "1,500.8" 같은 짝이 비교 불가(0) 였음 (WEB-1774)
function compareCellValue(aVal: string | number, bVal: string | number, orderMul: number): number {
  // duration 셀 포맷은 "12’ 34”"(전각 따옴표, getDurationString) — durationToNumber 의 구분자와 동일.
  // 기존엔 ASCII(') 로 검사해 이 분기가 발동한 적이 없었음 (WEB-1774)
  if (
    TypeChecker.isString(aVal) &&
    TypeChecker.isString(bVal) &&
    aVal.includes("’") &&
    bVal.includes("’")
  ) {
    return (durationToNumber(aVal) - durationToNumber(bVal)) * orderMul
  }

  const aNum = toNumericCell(aVal)
  const bNum = toNumericCell(bVal)
  if (!isNaN(aNum) && !isNaN(bNum)) {
    return (aNum - bNum) * orderMul
  }

  return 0
}

// Activity 테이블 전용 셀 비교(선수 테이블과 달리 date/activityName/activityType 처리).
export function compareActivityTableCell(
  aVal: string | number | null,
  bVal: string | number | null,
  field: string,
  orderMul: number,
): number {
  const missing = compareMissingCell(aVal, bVal)
  if (missing !== null) {
    return missing
  }

  if (field === "date") {
    return (new Date(aVal as string).getTime() - new Date(bVal as string).getTime()) * orderMul
  } else if (field === "activityName" || field === "activityType") {
    return orderMul * String(aVal).localeCompare(String(bVal))
  }

  return compareCellValue(aVal as string | number, bVal as string | number, orderMul)
}

export function compareTableCell(
  aVal: string | number | null,
  bVal: string | number | null,
  field: string,
  orderMul: number,
  positionRank: PositionRankFn,
): number {
  const missing = compareMissingCell(aVal, bVal)
  if (missing !== null) {
    return missing
  }

  if (field === "position") {
    return (positionRank(bVal ?? undefined) - positionRank(aVal ?? undefined)) * orderMul
  } else if (field === "playerName") {
    return orderMul * String(aVal).localeCompare(String(bVal))
  }

  return compareCellValue(aVal as string | number, bVal as string | number, orderMul)
}

// tableBody(배열형)를 정렬. summary(합계/평균) 행은 화면(StatsByPlayerSection)·원본 데이터(createStats
// 의 unshift)와 동일하게 최상단에 보존한다.
// 셀 비교는 섹션별 comparator 를 주입(선수=compareTableCell, 액티비티=compareActivityTableCell).
export function sortTableBody(
  tableBody: DataTable,
  tableHeader: { key: string }[],
  sort: ReportSortSetting,
  isSummaryRow: (row: TableRow) => boolean,
  compareCell: (
    aVal: string | number | null,
    bVal: string | number | null,
    field: string,
    orderMul: number,
  ) => number,
): DataTable {
  const colIndex = tableHeader.findIndex((header) => header.key === sort.key)
  if (colIndex === -1) {
    return tableBody
  }

  const summaryRows: TableRow[] = []
  const playerRows: TableRow[] = []
  tableBody.forEach((row) => {
    if (isSummaryRow(row)) {
      summaryRows.push(row)
    } else {
      playerRows.push(row)
    }
  })

  const orderMul = sort.order === "asc" ? 1 : -1
  const sortedPlayers = [...playerRows].sort((a, b) =>
    compareCell(a[colIndex], b[colIndex], sort.key, orderMul),
  )

  return [...summaryRows, ...sortedPlayers]
}

// export(PDF) 시 적용할 정렬 결정: 화면 정렬(store) 우선, 없으면 템플릿 정렬(sort) seed.
const resolveExportSort = (
  stored: ReportSortSetting | undefined,
  templateSort: Sort | undefined,
): ReportSortSetting | undefined => {
  if (stored) {
    return stored
  }

  if (templateSort?.key) {
    return { key: templateSort.key, order: templateSort.order.toLowerCase() as "asc" | "desc" }
  }

  return undefined
}

// 원본의 prototype 을 보존한 얕은 사본 생성.
// plain spread({...obj})는 클래스 인스턴스를 plain object 로 만들어
// instanceof 분기(예: MultipleChart 의 SessionDetailsData 판별)를 깨뜨린다.
const cloneWithProto = <T>(original: T, overrides: Partial<T>): T =>
  Object.assign(Object.create(Object.getPrototypeOf(original)), original, overrides)

// SectionGraph 를 export 정렬 적용한 사본으로 반환. 각 chart 를 store(chartIndex) → 템플릿 순으로 정렬.
export function sortGraphSectionForExport(
  graph: SectionGraph,
  positionRank: PositionRankFn,
  getStored: (chartIndex: number) => ReportSortSetting | undefined,
): SectionGraph {
  const charts = graph.charts.map((chart, chartIndex) => {
    const sort = resolveExportSort(getStored(chartIndex), chart.sort)
    if (!sort) {
      return chart
    }

    return cloneWithProto(chart, {
      data: sortChartRows(chart.data, chart.axisY, sort, positionRank),
    })
  })

  return cloneWithProto(graph, { charts })
}

// SectionSessionDetails 의 각 dataSession 을 export 정렬 적용한 사본으로 반환.
export function sortSessionDetailsForExport(
  section: SectionSessionDetails,
  positionRank: PositionRankFn,
  stored: ReportSortSetting | undefined,
): SectionSessionDetails {
  const first = section.sessionDetailsData?.[0]
  const sort = resolveExportSort(stored, first?.sortSession)
  if (!sort) {
    return section
  }

  const sessionDetailsData = section.sessionDetailsData.map((detail) =>
    cloneWithProto(detail, {
      dataSession: sortChartRows(detail.dataSession, detail.axisY, sort, positionRank),
    }),
  )

  return cloneWithProto(section, { sessionDetailsData })
}
